import type { Workload } from '@morales/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth';
import { BarList, TrendChart } from '../components/charts';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { Empty, ErrorBox, QueryView } from '../components/ui';
import { addDays, formatDate, formatQty, todayIso, weekdayName } from '../format';
import { useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

type Day = Workload['days'][number];
const SPANS = [7, 14, 28] as const;

/**
 * Reports → Delivery workload (decision 90): the days ahead with the stops, orders, cases and pick lines booked and
 * expected, a busy day for each, and the drivers each one takes, so JL can staff the trucks and the retail store from
 * the same people. The app proposes; JL schedules.
 */
export function WorkloadPage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const [spanRaw, setSpan] = useSearchParam('days');
  const span = SPANS.includes(Number(spanRaw) as (typeof SPANS)[number]) ? Number(spanRaw) : 14;
  const from = todayIso();
  const to = addDays(from, span - 1);
  const query = useQuery({ queryKey: qk.workload(from, to), queryFn: ({ signal }) => api.workload({ from, to }, signal), placeholderData: keepPreviousData });
  const n = (v: number) => formatQty(v);
  const dayLabel = (d: Day) => formatDate(d.date, intlLocale);

  const columns: SheetColumn<Day>[] = [
    { key: 'date', header: t('dateCol'), width: '120px', render: (d) => <strong>{dayLabel(d)}</strong> },
    { key: 'booked', header: t('wlBooked'), hint: t('wlBookedHint'), width: '70px', align: 'right', render: (d) => n(d.booked.stops) },
    { key: 'usual', header: t('wlUsual'), hint: t('wlUsualHint'), width: '70px', align: 'right', render: (d) => n(d.usual.stops) },
    { key: 'busy', header: t('wlBusy'), hint: t('wlBusyHint'), width: '70px', align: 'right', render: (d) => n(d.busy.stops) },
    { key: 'stops', header: t('wlStops'), hint: t('wlStopsHint'), width: '70px', align: 'right', render: (d) => <strong>{n(d.projected.stops)}</strong> },
    { key: 'orders', header: t('wlOrders'), width: '64px', align: 'right', render: (d) => n(d.projected.orders) },
    { key: 'cases', header: t('wlCases'), width: '64px', align: 'right', render: (d) => n(d.projected.cases) },
    { key: 'lines', header: t('wlLines'), hint: t('wlLinesHint'), width: '64px', align: 'right', render: (d) => n(d.projected.lines) },
    {
      key: 'drivers',
      header: t('wlDrivers'),
      hint: t('wlDriversHint'),
      width: '84px',
      align: 'right',
      render: (d) => (d.drivers === 0 && d.driversBusy === 0 ? '' : <strong>{d.driversBusy > d.drivers ? `${d.drivers}–${d.driversBusy}` : d.drivers}</strong>),
      cellClass: (d) => (d.driversBusy > d.drivers ? 'warn' : undefined),
    },
    {
      key: 'notes',
      header: t('wlNotes'),
      render: (d) => [d.holiday ? `★ ${d.holiday}` : '', d.notInYet.length ? `${t('wlNotInYet')}: ${d.notInYet.map((c) => c.customerName).join(', ')}` : ''].filter(Boolean).join(' · '),
    },
  ];

  return (
    <div className="desk-page" data-testid="workload">
      <header className="desk-head">
        <div>
          <h1>{t('workloadTitle')}</h1>
          <p className="muted">{t('workloadHint')}</p>
        </div>
      </header>
      <div className="toolbar">
        <div className="seg" role="group" aria-label={t('period')}>
          {SPANS.map((s) => (
            <button key={s} type="button" aria-pressed={span === s} onClick={() => setSpan(s === 14 ? '' : String(s))}>
              {t('wlNextDays').replace('{n}', String(s))}
            </button>
          ))}
        </div>
        {me?.role === 'owner' && query.data ? <StopsPerDriver current={query.data.stopsPerDriver} /> : null}
      </div>
      <QueryView query={query}>
        {(w) => {
          const ahead = w.days.filter((d) => d.date >= from);
          const busiest = ahead.reduce<Day | null>((best, d) => (!best || d.projected.stops > best.projected.stops ? d : best), null);
          const open = ahead.filter((d) => d.projected.stops > 0);
          return (
            <div className="stack" style={{ opacity: query.isPlaceholderData ? 0.6 : 1 }}>
              <div className="kpi-row" data-testid="workload-kpis">
                <div className="kpi">
                  <span className="kpi-label">{t('wlBusiest')}</span>
                  <span className="kpi-value">{busiest && busiest.projected.stops > 0 ? dayLabel(busiest) : '—'}</span>
                  <span className="kpi-delta">{busiest && busiest.projected.stops > 0 ? `${n(busiest.projected.stops)} ${t('wlStopsWord')} · ${busiest.driversBusy} ${t('wlDriversWord')}` : ''}</span>
                </div>
                <div className="kpi">
                  <span className="kpi-label">{t('wlMostDrivers')}</span>
                  <span className="kpi-value">{Math.max(0, ...ahead.map((d) => d.driversBusy))}</span>
                  <span className="kpi-delta">{t('wlAtStops').replace('{n}', formatQty(w.stopsPerDriver))}</span>
                </div>
                <div className="kpi">
                  <span className="kpi-label">{t('wlStopsAhead')}</span>
                  <span className="kpi-value">{n(Math.round(open.reduce((s, d) => s + d.projected.stops, 0)))}</span>
                  <span className="kpi-delta">{t('wlOverDays').replace('{n}', String(open.length))}</span>
                </div>
                <div className="kpi" title={t('wlAccuracyHint')}>
                  <span className="kpi-label">{t('wlAccuracy')}</span>
                  <span className="kpi-value">{w.accuracy ? `${w.accuracy.withinTwoPct}%` : '—'}</span>
                  <span className="kpi-delta">{w.accuracy ? t('wlOffBy').replace('{n}', formatQty(w.accuracy.meanStopsOff)) : t('wlNotEnough')}</span>
                </div>
              </div>
              {open.length === 0 ? (
                <Empty>{t('wlNothing')}</Empty>
              ) : (
                <div className="chart-grid">
                  <section className="wide" data-testid="workload-chart">
                    <h2>{t('wlStopsByDay')}</h2>
                    <TrendChart
                      label={t('wlStopsByDay')}
                      points={ahead.map((d) => ({ key: d.date, axis: `${weekdayName(d.weekday, intlLocale)} ${Number(d.date.slice(8))}`, title: dayLabel(d) }))}
                      bar={{ name: t('wlStops'), values: ahead.map((d) => d.projected.stops), color: 'var(--viz-1)' }}
                      line={{ name: t('wlBusy'), values: ahead.map((d) => d.busy.stops), color: 'var(--viz-2)' }}
                      format={(v) => `${formatQty(v)} ${t('wlStopsWord')}`}
                      axisFormat={(v) => formatQty(v)}
                      height={220}
                    />
                  </section>
                  <section data-testid="workload-weekdays">
                    <h2>{t('wlByWeekday')}</h2>
                    <BarList
                      label={t('wlByWeekday')}
                      rows={w.weekdays.map((x) => ({ key: String(x.weekday), label: weekdayName(x.weekday, intlLocale), value: x.usual.stops, display: formatQty(x.usual.stops), sub: x.busy.stops > x.usual.stops ? `${t('wlBusy')} ${formatQty(x.busy.stops)}` : undefined }))}
                    />
                  </section>
                </div>
              )}
              <Sheet label={t('workloadTitle')} columns={columns} rows={w.days} rowKey={(d) => d.date} rowClass={(d) => (d.holiday ? 'holiday' : undefined)} />
              <p className="muted">{t('wlFootnote').replace('{weeks}', String(w.weeksBack))}</p>
            </div>
          );
        }}
      </QueryView>
    </div>
  );
}

/** The owner's number: how many stops one driver covers in a day. */
function StopsPerDriver({ current }: { current: number }) {
  const { t } = useLocale();
  const qc = useQueryClient();
  const [value, setValue] = useState(String(current));
  useEffect(() => setValue(String(current)), [current]);
  const save = useMutation({
    mutationFn: (v: number) => api.saveWorkloadSettings({ stopsPerDriver: v }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['reports', 'workload'] }),
  });
  const n = Number(value);
  const ok = Number.isFinite(n) && n > 0 && n <= 100;
  return (
    <form
      className="tool"
      onSubmit={(e) => {
        e.preventDefault();
        if (ok && n !== current) save.mutate(n);
      }}
    >
      <label className="tool">
        <span>{t('wlStopsPerDriver')}</span>
        <input type="number" min={1} max={100} step={1} inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value)} style={{ width: '5em' }} aria-invalid={!ok || undefined} />
      </label>
      <button type="submit" className="btn btn-secondary btn-sm" disabled={!ok || n === current || save.isPending}>
        {t('vsSave')}
      </button>
      {save.error ? <ErrorBox error={save.error} /> : null}
    </form>
  );
}
